import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from '@tanstack/react-router'
import { useErp } from '../api'
import type { EntityType } from '../contract'
import { ENTITY_META } from './entity'

/* ERP · prévia ao passar o mouse (ou focar) num EntityLink: tipo, nome e 2–3 linhas (q.peekLines). Aparece depois de
   ~350 ms, não pega o foco nem o mouse (pointer-events: none) e some no Esc, ao sair, no blur, ao rolar e ao trocar
   de tela. Um só no app (montado no AppShell); sem o grafo carregado, não aparece. */

type Peek = { type: EntityType; id: string; rect: DOMRect }

let current: Peek | null = null
let timer: number | undefined
const listeners = new Set<() => void>()
const emit = () => { for (const l of listeners) l() }
const subscribe = (l: () => void) => {
    listeners.add(l)
    return () => listeners.delete(l)
}

export const PEEK_ID = 'cf-entity-peek'
export const PEEK_DELAY = 350

export function showPeek(type: EntityType, id: string, el: HTMLElement) {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => {
        if (!el.isConnected) return
        current = { type, id, rect: el.getBoundingClientRect() }
        emit()
    }, PEEK_DELAY)
}

export function hidePeek() {
    window.clearTimeout(timer)
    if (!current) return
    current = null
    emit()
}

/** Handlers for the element that opens the preview (EntityLink). */
export function peekHandlers(type: EntityType, id: string) {
    const show = (e: React.SyntheticEvent<HTMLElement>) => showPeek(type, id, e.currentTarget)
    return { onMouseEnter: show, onFocus: show, onMouseLeave: hidePeek, onBlur: hidePeek, onClick: hidePeek }
}

const WIDTH = 260

export function EntityPeek() {
    const peek = useSyncExternalStore(subscribe, () => current, () => null)
    const { pathname } = useLocation()

    useEffect(() => hidePeek(), [pathname])
    useEffect(() => {
        if (!peek) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') hidePeek() }
        document.addEventListener('keydown', onKey)
        window.addEventListener('scroll', hidePeek, { passive: true, capture: true })
        window.addEventListener('resize', hidePeek)
        return () => {
            document.removeEventListener('keydown', onKey)
            window.removeEventListener('scroll', hidePeek, { capture: true })
            window.removeEventListener('resize', hidePeek)
        }
    }, [peek])

    return peek ? <PeekCard peek={peek} /> : null
}

function PeekCard({ peek }: { peek: Peek }) {
    const { q } = useErp()
    const ref = useRef<HTMLDivElement>(null)
    const [top, setTop] = useState<number | null>(null)
    const e = q?.get(peek.type, peek.id)
    const left = Math.max(8, Math.min(window.innerWidth - WIDTH - 8, peek.rect.left))

    useLayoutEffect(() => {
        const h = ref.current?.offsetHeight ?? 0
        setTop(peek.rect.bottom + 8 + h > window.innerHeight ? Math.max(8, peek.rect.top - h - 8) : peek.rect.bottom + 8)
    }, [peek, e])

    if (!q || !e) return null
    let lines: string[] = []
    try {
        lines = q.peekLines(e) ?? []
    } catch {
        lines = []
    }
    const meta = ENTITY_META[peek.type]
    return createPortal(
        <div
            ref={ref}
            id={PEEK_ID}
            role="tooltip"
            className="pointer-events-none fixed z-50 flex flex-col gap-1 rounded-[12px] border border-line bg-panel p-3 text-[12.5px]"
            style={{ left, top: top ?? -9999, width: WIDTH, boxShadow: '0 14px 34px var(--shadow)', visibility: top === null ? 'hidden' : undefined }}
        >
            <span className="text-[11px] font-semibold tracking-[.05em] uppercase" style={{ color: meta.color }}>{meta.label}</span>
            <b className="text-[14px] text-fg">{e.name}</b>
            {lines.map((l, i) => <span key={i} className="text-muted">{l}</span>)}
        </div>,
        document.body,
    )
}
